import React, { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Activity, TrendingDown, TrendingUp, Minus } from 'lucide-react';

/**
 * Custom tooltip for the retention chart
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  
  const data = payload[0].payload;
  
  return (
    <div className="bg-popover/95 backdrop-blur border rounded-lg shadow-lg p-3 min-w-[160px]">
      <div className="flex items-center justify-between gap-3 mb-1">
        <span className="text-xs text-muted-foreground">{data.time.toFixed(1)}s</span>
        {data.is_cut && (
          <Badge variant="outline" className="text-xs py-0">Schnitt</Badge>
        )}
      </div>
      <div className="flex items-center gap-2">
        <div 
          className="w-3 h-3 rounded-full" 
          style={{ backgroundColor: getEngagementColor(data.engagement) }}
        />
        <span className="font-semibold">{data.engagement}%</span>
        <span className="text-xs text-muted-foreground">Engagement</span>
      </div>
      {data.label && (
        <div className="mt-1 pt-1 border-t text-sm font-medium">{data.label}</div>
      )}
      {data.scene_type && data.scene_type !== 'unknown' && (
        <div className="text-xs text-muted-foreground capitalize">{data.scene_type.replace('_', ' ')}</div>
      )}
    </div>
  );
};

/**
 * Get color based on engagement value
 */
const getEngagementColor = (engagement) => {
  if (engagement >= 80) return 'hsl(var(--chart-1))';
  if (engagement >= 60) return 'hsl(var(--chart-2))';
  if (engagement >= 40) return 'hsl(var(--chart-3))';
  return 'hsl(var(--chart-4))';
};

/**
 * Calculate engagement trend
 */
const calculateTrend = (data) => {
  if (!data || data.length < 2) return { direction: 'stable', percentage: 0 };
  
  const firstHalf = data.slice(0, Math.floor(data.length / 2));
  const secondHalf = data.slice(Math.floor(data.length / 2));
  
  const avgFirst = firstHalf.reduce((sum, d) => sum + d.engagement, 0) / firstHalf.length;
  const avgSecond = secondHalf.reduce((sum, d) => sum + d.engagement, 0) / secondHalf.length;
  
  const change = ((avgSecond - avgFirst) / avgFirst) * 100;
  
  if (change > 5) return { direction: 'up', percentage: Math.abs(change).toFixed(1) };
  if (change < -5) return { direction: 'down', percentage: Math.abs(change).toFixed(1) };
  return { direction: 'stable', percentage: Math.abs(change).toFixed(1) };
};

/**
 * RetentionChart - Visualizes video pacing and engagement over time
 */
export function RetentionChart({ pacingData, videoDuration, className = '' }) {
  // Process data for chart
  const chartData = useMemo(() => {
    if (!pacingData || pacingData.length === 0) {
      // Generate sample data if none provided
      const duration = videoDuration || 30;
      return Array.from({ length: Math.ceil(duration / 2) + 1 }, (_, i) => {
        const time = i * 2;
        let engagement = 85;
        if (time <= 3) engagement = 95 - (time * 2);
        else if (time <= duration * 0.3) engagement = 75 - (time * 0.5);
        else if (time <= duration * 0.7) engagement = 60 + Math.sin(time) * 10;
        else engagement = 55 + Math.sin(time) * 5;
        
        return {
          time,
          engagement: Math.max(30, Math.min(100, Math.round(engagement))),
          label: time === 0 ? 'Start' : time <= 3 ? 'Hook' : '',
          scene_type: 'unknown',
          is_cut: false
        };
      });
    }
    
    return pacingData.map(point => ({
      time: point.time || 0,
      engagement: point.engagement || 50,
      label: point.label || '',
      scene_type: point.scene_type || 'unknown',
      is_cut: point.is_cut || false
    }));
  }, [pacingData, videoDuration]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;
    
    const engagements = chartData.map(d => d.engagement);
    const avgEngagement = Math.round(engagements.reduce((a, b) => a + b, 0) / engagements.length);
    const maxEngagement = Math.max(...engagements);
    const minEngagement = Math.min(...engagements);
    const trend = calculateTrend(chartData);
    const cutPoints = chartData.filter(d => d.is_cut).length;
    
    return { avgEngagement, maxEngagement, minEngagement, trend, cutPoints };
  }, [chartData]);

  // Find labeled points for reference dots
  const labeledPoints = useMemo(() => {
    return chartData.filter(d => d.label && d.label.length > 0);
  }, [chartData]);

  if (!chartData || chartData.length === 0) {
    return null;
  }

  return (
    <Card className={`${className}`} data-testid="retention-chart">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Pacing & Retention</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {stats?.trend && (
              <Badge 
                variant="secondary" 
                className={`text-xs ${
                  stats.trend.direction === 'up' 
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400' 
                    : stats.trend.direction === 'down'
                    ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                    : 'bg-muted'
                }`}
              >
                {stats.trend.direction === 'up' ? (
                  <TrendingUp className="h-3 w-3 mr-1" />
                ) : stats.trend.direction === 'down' ? (
                  <TrendingDown className="h-3 w-3 mr-1" />
                ) : (
                  <Minus className="h-3 w-3 mr-1" />
                )}
                {stats.trend.percentage}%
              </Badge>
            )}
            {videoDuration > 0 && (
              <Badge variant="outline" className="text-xs">
                {videoDuration.toFixed(1)}s
              </Badge>
            )}
          </div>
        </div>
        <CardDescription>
          Geschätztes Engagement über die Videolänge
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div className="text-center p-2 rounded-lg bg-muted/30">
              <div className="text-lg font-bold text-primary">{stats.avgEngagement}%</div>
              <div className="text-xs text-muted-foreground">Durchschnitt</div>
            </div>
            <div className="text-center p-2 rounded-lg bg-muted/30">
              <div className="text-lg font-bold text-green-600 dark:text-green-400">{stats.maxEngagement}%</div>
              <div className="text-xs text-muted-foreground">Maximum</div>
            </div>
            <div className="text-center p-2 rounded-lg bg-muted/30">
              <div className="text-lg font-bold text-orange-600 dark:text-orange-400">{stats.minEngagement}%</div>
              <div className="text-xs text-muted-foreground">Minimum</div>
            </div>
            <div className="text-center p-2 rounded-lg bg-muted/30">
              <div className="text-lg font-bold text-accent">{stats.cutPoints}</div>
              <div className="text-xs text-muted-foreground">Schnitte</div>
            </div>
          </div>
        )}
        
        {/* Chart */}
        <div className="h-[200px] w-full min-h-[200px]">
          <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={100}>
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
            >
              <defs>
                <linearGradient id="engagementGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="hsl(var(--border))" 
                opacity={0.5}
                vertical={false}
              />
              <XAxis 
                dataKey="time" 
                tickFormatter={(value) => `${value}s`}
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                width={45}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Reference line for average */}
              {stats && (
                <ReferenceLine 
                  y={stats.avgEngagement} 
                  stroke="hsl(var(--muted-foreground))" 
                  strokeDasharray="5 5"
                  opacity={0.5}
                />
              )}
              
              {/* Cut point markers */}
              {chartData.filter(d => d.is_cut).map((point, idx) => (
                <ReferenceLine
                  key={`cut-${idx}`}
                  x={point.time}
                  stroke="hsl(var(--accent))"
                  strokeWidth={2}
                  opacity={0.6}
                />
              ))}
              
              <Area
                type="monotone"
                dataKey="engagement"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill="url(#engagementGradient)"
                animationDuration={1000}
              />
              
              {/* Labeled points as dots */}
              {labeledPoints.map((point, idx) => (
                <ReferenceDot
                  key={`label-${idx}`}
                  x={point.time}
                  y={point.engagement}
                  r={5}
                  fill="hsl(var(--primary))"
                  stroke="hsl(var(--background))"
                  strokeWidth={2}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* Legend */}
        <div className="flex items-center justify-center gap-4 mt-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <div className="w-3 h-0.5 bg-primary rounded" />
            <span>Engagement</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 border-2 border-accent rounded-sm" />
            <span>Schnitt</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-0.5 bg-muted-foreground/50 rounded" style={{ backgroundImage: 'repeating-linear-gradient(90deg, currentColor 0 3px, transparent 3px 6px)' }} />
            <span>Durchschnitt</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default RetentionChart;
